import {ExpandLess, ExpandMore, School} from '@mui/icons-material';
import {Box, CardHeader, IconButton, LinearProgress, Stack, Typography} from '@mui/material';
import {ModuleTopic, Progress} from '@cp/model';
import CourseCategoryTopicCardHeaderProgress from './CourseCategoryTopicCardHeaderProgress';
import extractProgressStudyProgress from './extractProgressStudyProgress';

export interface CourseCategoryTopicCardHeaderProps {
  topic: ModuleTopic;
  subjectColor: {bg: string; border: string; icon: string};
  progressScore?: number;
  progress?: Progress;
  showContent?: boolean;
  onToggleContent?: () => void;
}

export default function CourseCategoryTopicCardHeader({
  topic,
  subjectColor,
  progressScore,
  progress,
  showContent,
  onToggleContent,
}: CourseCategoryTopicCardHeaderProps) {
  const topicScore = progressScore || 0;
  const studyProgress = extractProgressStudyProgress(progress, topic);
  return (
    <CardHeader
      avatar={
        <Box
          sx={{
            bgcolor: subjectColor.bg,
            borderRadius: 1,
            p: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            minWidth: 40,
            height: 40,
          }}
        >
          <School sx={{fontSize: 20, color: subjectColor.border}} />
        </Box>
      }
      title={
        <Typography variant="subtitle1" sx={{fontWeight: 600, lineHeight: 1.3}} data-testid="course-topic-name">
          {topic.topic}
        </Typography>
      }
      subheader={
        <Stack spacing={0.5} sx={{mt: 0.5}}>
          {progressScore !== undefined && (
            <Box>
              <CourseCategoryTopicCardHeaderProgress score={topicScore} />
            </Box>
          )}
          {studyProgress > 0 && (
            <Box data-testid="course-topic-study-progress">
              <Box sx={{display: 'flex', alignItems: 'center', gap: 1, mb: 0.5}}>
                <Typography variant="caption" color="text.secondary" sx={{fontSize: '0.7rem'}}>
                  Study Progress
                </Typography>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{fontSize: '0.7rem', fontWeight: 600}}
                  data-testid="course-topic-study-progress-percentage"
                >
                  {studyProgress}%
                </Typography>
              </Box>
              <LinearProgress
                variant="determinate"
                value={studyProgress}
                data-testid="course-topic-study-progress-bar"
                sx={{
                  height: 6,
                  borderRadius: 1,
                  bgcolor: 'grey.200',
                  '& .MuiLinearProgress-bar': {
                    borderRadius: 1,
                    bgcolor: subjectColor.border,
                  },
                }}
              />
            </Box>
          )}
        </Stack>
      }
      action={
        onToggleContent ? (
          <IconButton onClick={onToggleContent} sx={{color: subjectColor.border}}>
            {showContent ? <ExpandLess /> : <ExpandMore />}
          </IconButton>
        ) : null
      }
      sx={{
        bgcolor: 'white',
        '&:hover': {
          bgcolor: `${subjectColor.bg}20`,
        },
      }}
    />
  );
}
